import { Avatar } from '@chakra-ui/react'
import { useEffect, useRef, useState } from 'react'
import { FiChevronDown, FiSearch, FiX } from 'react-icons/fi'

/**
 * AutocompleteSelect Component
 * Searchable select with pagination, Jira 2026 styling
 *
 * @param {string} label - Field label
 * @param {string} placeholder - Placeholder text
 * @param {string|Object} value - Selected value (item id or full item object)
 * @param {Function} onChange - Change handler (item) => void
 * @param {Function} fetchOptions - Async function to fetch options ({ search, page, page_size }) => Promise<{ results: [], count: number }>
 * @param {Function} renderOption - Custom render for option item (item, isSelected) => ReactNode
 * @param {Function} getOptionLabel - Get label from item (item) => string
 * @param {Function} getOptionValue - Get value from item (item) => string
 * @param {string} error - Error message
 * @param {boolean} disabled - Disabled state
 * @param {boolean} showAvatar - Show avatar for users
 * @param {string} emptyText - Text when no results
 * @param {boolean} autoOpen - Automatically open dropdown on mount
 */
export function AutocompleteSelect({
  label,
  placeholder = 'Search...',
  value,
  onChange,
  fetchOptions,
  renderOption,
  getOptionLabel = (item) => item.name || item.username || `${item.first_name} ${item.last_name}`,
  getOptionValue = (item) => item.id,
  error,
  disabled = false,
  showAvatar = false,
  emptyText = 'No results found',
  autoOpen = false,
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [options, setOptions] = useState([])
  const [selectedItem, setSelectedItem] = useState(null)
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [totalCount, setTotalCount] = useState(0)

  const wrapperRef = useRef(null)
  const listRef = useRef(null)
  const searchInputRef = useRef(null)
  const requestIdRef = useRef(0)

  const PAGE_SIZE = 20

  // Sync selectedItem with value prop (only when component is controlled)
  useEffect(() => {
    // If value prop is not provided (undefined), don't sync - allow internal state
    if (value === undefined) {
      return
    }

    if (value && typeof value === 'object') {
      // Value is a full object
      setSelectedItem(value)
    } else if (value && typeof value !== 'object') {
      // Value is just an ID - we can't display it without fetching, so keep null
      // The parent should pass the full object if available
      setSelectedItem(null)
    } else {
      // Value is explicitly null - clear selection
      setSelectedItem(null)
    }
  }, [value])

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Fetch options
  async function loadOptions(searchQuery = '', pageNum = 1, append = false, requestId) {
    try {
      setLoading(true)
      const result = await fetchOptions({
        search: searchQuery,
        page: pageNum,
        page_size: PAGE_SIZE,
      })

      // Ignore stale responses
      if (requestId !== requestIdRef.current) {
        return
      }

      // API format: { data: [...], meta: { pagination: { total_count: N, has_next: bool } } }
      const newOptions = result.data || []
      const count = result.meta?.pagination?.total_count || 0
      const hasNextPage = result.meta?.pagination?.has_next || false

      setOptions(prev => append ? [...prev, ...newOptions] : newOptions)
      setTotalCount(count)
      setHasMore(hasNextPage)
    } catch (error) {
      // Ignore errors from stale requests
      if (requestId !== requestIdRef.current) {
        return
      }
      // Failed to load - reset to empty state
      setOptions([])
      setTotalCount(0)
      setHasMore(false)
    } finally {
      setLoading(false)
    }
  }

  // Initial load and search with debounce
  useEffect(() => {
    if (!isOpen) return

    // Clear options immediately when search changes (but not on initial load)
    setOptions([])

    // Reset page
    setPage(1)

    // Increment request ID to invalidate previous requests
    requestIdRef.current += 1
    const currentRequestId = requestIdRef.current

    // Debounce search (150ms for responsive feel)
    const timeoutId = setTimeout(() => {
      loadOptions(search, 1, false, currentRequestId)
    }, 150)

    return () => clearTimeout(timeoutId)
  }, [isOpen, search])

  // Focus search input when opened
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [isOpen])

  // Auto-open on mount
  useEffect(() => {
    if (autoOpen && !disabled) {
      setIsOpen(true)
    }
  }, [autoOpen, disabled])

  // Handle scroll for pagination
  function handleScroll(e) {
    const bottom = e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight + 10
    if (bottom && hasMore && !loading) {
      setPage(prevPage => {
        const nextPage = prevPage + 1
        loadOptions(search, nextPage, true, requestIdRef.current)
        return nextPage
      })
    }
  }

  // Handle select
  function handleSelect(item) {
    setSelectedItem(item)
    onChange(item)
    setIsOpen(false)
    setSearch('')
  }

  // Handle clear
  function handleClear(e) {
    e.stopPropagation()
    setSelectedItem(null)
    onChange(null)
    setSearch('')
  }

  // Get display value
  const displayValue = selectedItem ? getOptionLabel(selectedItem) : ''

  // Default option render
  const defaultRenderOption = (item, isSelected) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
      {showAvatar && (
        <Avatar.Root size="xs" style={{ width: '24px', height: '24px', flexShrink: 0 }}>
          <Avatar.Image src={item.avatar} />
          <Avatar.Fallback>
            {(item.first_name?.[0] || '') + (item.last_name?.[0] || '') || item.username?.[0]?.toUpperCase() || '?'}
          </Avatar.Fallback>
        </Avatar.Root>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: isSelected ? '600' : '400', color: '#172B4D', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {getOptionLabel(item)}
        </div>
        {(item.email || item.phone_first) && (
          <div style={{ fontSize: '12px', color: '#5E6C84', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {item.email || item.phone_first}
          </div>
        )}
      </div>
    </div>
  )

  return (
    <div style={{ position: 'relative', width: '100%' }} ref={wrapperRef}>
      {/* Label */}
      {label && (
        <label
          style={{
            display: 'block',
            fontSize: '12px',
            fontWeight: '600',
            color: '#5E6C84',
            marginBottom: '4px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          {label}
        </label>
      )}

      {/* Input */}
      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          height: '36px',
          padding: '0 32px 0 12px',
          backgroundColor: disabled ? '#F4F5F7' : '#FAFBFC',
          border: `1px solid ${error ? '#DE350B' : isOpen ? '#4C9AFF' : '#DFE1E6'}`,
          borderRadius: '3px',
          fontSize: '14px',
          color: '#172B4D',
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'border-color 0.2s, box-shadow 0.2s',
          boxShadow: isOpen ? '0 0 0 1px #4C9AFF' : 'none',
        }}
      >
        {selectedItem ? (
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {displayValue}
          </span>
        ) : (
          <span style={{ flex: 1, color: '#5E6C84' }}>{placeholder}</span>
        )}

        {/* Clear button */}
        {selectedItem && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            style={{
              position: 'absolute',
              right: '28px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '20px',
              height: '20px',
              padding: 0,
              border: 'none',
              background: 'transparent',
              color: '#5E6C84',
              cursor: 'pointer',
            }}
          >
            <FiX size={14} />
          </button>
        )}

        {/* Dropdown icon */}
        <div
          style={{
            position: 'absolute',
            right: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            color: '#5E6C84',
            transition: 'transform 0.2s',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        >
          <FiChevronDown size={16} />
        </div>
      </div>

      {/* Error */}
      {error && (
        <div style={{ marginTop: '4px', fontSize: '12px', color: '#DE350B' }}>
          {error}
        </div>
      )}

      {/* Dropdown */}
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            zIndex: 1000,
            backgroundColor: 'white',
            border: '1px solid #DFE1E6',
            borderRadius: '3px',
            boxShadow: '0 4px 8px 0 rgba(9, 30, 66, 0.08), 0 0 1px 0 rgba(9, 30, 66, 0.08)',
            maxHeight: '320px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Search input */}
          <div
            style={{
              position: 'relative',
              padding: '8px',
              borderBottom: '1px solid #EBECF0',
            }}
          >
            <div
              style={{
                position: 'absolute',
                left: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#5E6C84',
                pointerEvents: 'none',
              }}
            >
              <FiSearch size={14} />
            </div>
            <input
              ref={searchInputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              style={{
                width: '100%',
                height: '32px',
                padding: '0 12px 0 32px',
                backgroundColor: '#FAFBFC',
                border: '1px solid #DFE1E6',
                borderRadius: '3px',
                fontSize: '14px',
                color: '#172B4D',
                outline: 'none',
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {/* Options list */}
          <div
            ref={listRef}
            onScroll={handleScroll}
            style={{
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
            }}
          >
            {loading && options.length === 0 ? (
              <div style={{ padding: '12px 16px', textAlign: 'center', color: '#5E6C84', fontSize: '14px' }}>
                Loading...
              </div>
            ) : options.length === 0 ? (
              <div style={{ padding: '12px 16px', textAlign: 'center', color: '#5E6C84', fontSize: '14px' }}>
                {emptyText}
              </div>
            ) : (
              <>
                {options.map((item) => {
                  const itemValue = getOptionValue(item)
                  // Support both object and primitive value
                  const valueToCompare = typeof value === 'object' && value !== null
                    ? getOptionValue(value)
                    : value
                  const isSelected = valueToCompare && itemValue && String(valueToCompare) === String(itemValue)

                  return (
                    <div
                      key={itemValue}
                      onClick={() => handleSelect(item)}
                      style={{
                        padding: '8px 12px',
                        cursor: 'pointer',
                        backgroundColor: isSelected ? '#DEEBFF' : 'transparent',
                        transition: 'background-color 0.1s',
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) e.currentTarget.style.backgroundColor = '#F4F5F7'
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent'
                      }}
                    >
                      {renderOption ? renderOption(item, isSelected) : defaultRenderOption(item, isSelected)}
                    </div>
                  )
                })}

                {/* Loading more indicator */}
                {loading && options.length > 0 && (
                  <div style={{ padding: '8px 16px', textAlign: 'center', color: '#5E6C84', fontSize: '12px' }}>
                    Loading more...
                  </div>
                )}

                {/* Results count */}
                {!loading && totalCount > 0 && (
                  <div style={{ padding: '8px 16px', textAlign: 'center', color: '#5E6C84', fontSize: '12px', borderTop: '1px solid #EBECF0' }}>
                    {options.length} of {totalCount}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default AutocompleteSelect
